import React, {
    lazy,
    memo,
    PropsWithChildren,
    Suspense,
    useCallback,
    MemoExoticComponent,
    useState,
    useMemo,
} from 'react';
import { getRealAuthHeaders, getRealUrlPrefix, useApiClient } from '@m43/core';
import { FederatedSearchResource, UnitResource } from '@mark43/cad-api';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ApiQueryFormResponse } from 'mark43-federated-search';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { AppName } from '../app-name/app-name';
import {
    FederatedQueryIdsContext,
    FederatedSearchNotificationsContext,
    FederatedSearchNotificationsContextValue,
    FederatedSearchQueryFormsContext,
    federatedSearchQueryFormsContextDefaultValue,
    FederatedSearchActionsContext,
} from './federated-search-context';
import { useGetCadEventOptions } from './hooks/use-get-cad-event-options';
import { useOpenDepartmentLink } from '../layout/use-open-department-link';
import { appNameToApplicationScope } from './helpers';
import { getDexMetadata } from '~/data-exchange/get-dex-metadata';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useQuery } from '@tanstack/react-query';
import { FederatedSearchNotificationsSync } from './federated-search-notifications-sync';

const FallbackComponent: MemoExoticComponent<
    (props: PropsWithChildren<Record<string, unknown>>) => React.JSX.Element
> = memo(({ children }) => <>{children}</>);

const FederatedSearchProvider = lazy(() =>
    import('federated-search/FederatedSearchProvider')
        .then((module) => ({
            default: module.FederatedSearchProvider,
        }))
        .catch(() => ({
            default: FallbackComponent,
        }))
);

const FederatedSearchApiControl = React.lazy(() =>
    import('federated-search/FederatedSearchApiControl').then((module) => ({
        default: module.FederatedSearchApiControl,
    }))
);

type FederatedSearchProviderProps = Parameters<typeof FederatedSearchProvider>[0];

type SubmitParams = Parameters<NonNullable<FederatedSearchProviderProps['onQueryFormSubmit']>>[0];

const notificationsDefaultValue: FederatedSearchNotificationsContextValue = {
    unreadQueryCount: 0,
    unreadQueriesIncludeSpecialResult: false,
    markAllQueriesAsRead: async () => {},
};

export const FederatedSearchProviderWrapper = ({
    children,
    appName,
}: React.PropsWithChildren<{ appName: AppName }>) => {
    const { userProfileView } = useBootstrapState();
    const { dexNextEnabled } = useEnabledFeatures();
    const apiClient = useApiClient();
    const urlPrefix = getRealUrlPrefix();
    const { getCadEventOptions } = useGetCadEventOptions();

    const getDeviceNameFallback = useCallback(async () => {
        const { deviceName } = await getDexMetadata();
        return deviceName;
    }, []);

    const openDepartmentLink = useOpenDepartmentLink();

    const openProfileLink = useCallback(
        (entityPath: string, profileId: number) => {
            openDepartmentLink({
                path: `profiles/${entityPath}/${profileId}`,
                app: 'RMS',
                tenantStrategy: 'PREFER_RMS_TENANT',
            });
        },
        [openDepartmentLink]
    );

    const [federatedSearchReadDataContextValue, setFederatedSearchReadDataContextValue] = useState(
        federatedSearchQueryFormsContextDefaultValue
    );
    const [federatedQueryIds, setFederatedQueryIds] = useState<string[]>([]);
    const [notificationsContextValue, setNotificationsContextValue] =
        useState<FederatedSearchNotificationsContextValue>(notificationsDefaultValue);

    const requestHeaders = useMemo(() => getRealAuthHeaders(), []);

    const getCadUnitOptions = useCallback(async () => {
        const response = await apiClient.request<UnitResource.GetAllUnits>({
            url: 'unit',
            method: 'GET',
        });
        return response.data.data?.map((unit) => ({ value: unit.id, label: unit.callSign })) || [];
    }, [apiClient]);

    const onQueryFormSubmit = useCallback(
        (querySubmission: SubmitParams) => {
            apiClient.request<FederatedSearchResource.SaveFederatedQuery>({
                method: 'POST',
                url: 'federated_search/save_federated_search_query/',
                data: {
                    federatedQueryId: querySubmission.federatedQuery.id,
                    linkToAssignedEvent: querySubmission.linkCurrentAssignedEvent,
                    queryIds:
                        querySubmission.federatedQuery.queries?.map((query) => query.id) ?? [],
                    eventId: querySubmission.contextInfo.CAD_EVENT_SELECT_VALUE,
                    unitId: querySubmission.contextInfo.CAD_UNIT_SELECT_VALUE,
                    parsedRawQuery: querySubmission.federatedQuery.parsedRawQuery,
                    queryableEntityType: querySubmission.federatedQuery.queryableEntityType,
                },
            });
        },
        [apiClient]
    );

    const federatedSearchNotificationsQuery = useQuery({
        enabled: dexNextEnabled,
        queryKey: ['federated-search-notifications'],
        queryFn() {
            return apiClient.request<FederatedSearchResource.FederatedSearchBootstrap>({
                method: 'POST',
                url: 'federated_search/bootstrap/',
            });
        },
        onSuccess(data) {
            const state = data.data.data?.federatedSearchUserQueryMetadata;
            const nextFederatedQueryIds = state?.federatedSearchQueryBundle?.federatedQueryIds ?? [];
            setFederatedQueryIds(nextFederatedQueryIds);
        },
    });

    useIntegratedStreams({
        enabled: dexNextEnabled,
        streamNames: ['federatedSearchUserQueryMetadata'],
        query: federatedSearchNotificationsQuery,
        onMessage(message) {
            if (message.payload.refetch) {
                federatedSearchNotificationsQuery.refetch();
            }
        },
    });

    const handleQueryFormsLoaded = useCallback((queryForms: ApiQueryFormResponse[]) => {
        setFederatedSearchReadDataContextValue((prev) => ({
            ...prev,
            queryForms,
        }));
    }, []);

    if (!dexNextEnabled) {
        return <>{children}</>;
    }

    return (
        <FederatedSearchQueryFormsContext.Provider value={federatedSearchReadDataContextValue}>
            <FederatedQueryIdsContext.Provider value={federatedQueryIds}>
                <FederatedSearchNotificationsContext.Provider value={notificationsContextValue}>
                    <Suspense fallback={<LoadingIndicator />}>
                        <FederatedSearchProvider
                            requestHeaders={requestHeaders}
                            onQueryFormSubmit={onQueryFormSubmit}
                            userProfile={userProfileView}
                            urlPrefix={urlPrefix}
                            getCadUnitOptions={getCadUnitOptions}
                            getCadEventOptions={getCadEventOptions}
                            onQueryFormsLoaded={handleQueryFormsLoaded}
                            applicationScope={appNameToApplicationScope(appName)}
                            getDeviceNameFallback={getDeviceNameFallback}
                            openProfileLink={openProfileLink}
                        >
                            <Suspense fallback={null}>
                                <FederatedSearchNotificationsSync
                                    onUpdate={setNotificationsContextValue}
                                />
                            </Suspense>
                            <FederatedSearchApiControl>
                                {({ setSelectedForm, setQueryFormValues }) => (
                                    <FederatedSearchActionsContext.Provider
                                        value={{ setSelectedForm, setQueryFormValues }}
                                    >
                                        {children}
                                    </FederatedSearchActionsContext.Provider>
                                )}
                            </FederatedSearchApiControl>
                        </FederatedSearchProvider>
                    </Suspense>
                </FederatedSearchNotificationsContext.Provider>
            </FederatedQueryIdsContext.Provider>
        </FederatedSearchQueryFormsContext.Provider>
    );
};
